import { ComponentType, MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { IconX } from '../../common/icons/cdl/X';
import { Box } from '../../common/ui/Box';
import { theme } from '../../common/ui/theme';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';

import { defaultVariants } from './util/defaultVariants';
import { disabledVariants } from './util/disabledVariants';
import { emphasisVariants } from './util/emphasisVariants';

const TruncatedText = styled(Text)`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const RemoveButton = styled.button`
    padding: 0;
    border: none;
    background: none;
    color: inherit;
    cursor: pointer;
    height: 12px;
    width: 12px;

    &:disabled {
        cursor: default;
    }
`;

interface TagComponentProps extends Pick<TagProps, 'variant' | 'emphasis' | 'disabled'> {}

const TagComponent = styled.div<TagComponentProps>`
    box-sizing: border-box;
    display: inline-flex;
    max-width: 100%;
    height: 20px;
    padding: calc(${theme.space[2]} - 1px) calc(${theme.space[4]} - 1px);
    border: 1px solid transparent;
    border-radius: ${theme.radii[4]};

    ${(props) => (props.emphasis ? emphasisVariants : defaultVariants)}
    ${(props) => (props.disabled ? disabledVariants : null)}
`;

type EnsureIconOrChildren =
    | {
          Icon: ComponentType<{ width?: number; height?: number }>;
          children?: never;
      }
    | {
          Icon?: never;
          children: ReactNode;
      }
    | {
          Icon: ComponentType<{ width?: number; height?: number }>;
          children: ReactNode;
      };

type SharedTagProps = {
    variant?: 'default' | 'accent' | 'positive' | 'attention' | 'negative' | 'special';
    emphasis?: boolean;
    disabled?: boolean;
    onRemoveClick?: (event: MouseEvent<HTMLButtonElement>) => void;
};

export type TagProps = SharedTagProps & EnsureIconOrChildren;

export const Tag = ({ children, onRemoveClick, Icon, variant = 'default', ...rest }: TagProps) => {
    const textRef = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);
    const tagComponentProps = { ...rest, variant };

    useEffect(() => {
        if (textRef.current) {
            setIsTruncated(textRef.current.scrollWidth > textRef.current.clientWidth);
        }
    }, []);

    if (isTruncated) {
        return (
            <Tooltip label={children} showArrow>
                <TagComponent {...tagComponentProps}>
                    <Box display="flex" alignItems="center" maxWidth="100%" height="16px">
                        {Icon ? (
                            <div>
                                <Icon height={12} width={12} />
                            </div>
                        ) : null}

                        {children ? (
                            <TruncatedText
                                ref={textRef}
                                variant="extraSmall"
                                fontWeight={500}
                                paddingX={2}
                                display="block"
                            >
                                {children}
                            </TruncatedText>
                        ) : null}

                        {onRemoveClick ? (
                            <RemoveButton
                                type="button"
                                disabled={rest.disabled}
                                onClick={rest.disabled ? undefined : (e) => onRemoveClick(e)}
                            >
                                <IconX height={12} width={12} />
                            </RemoveButton>
                        ) : null}
                    </Box>
                </TagComponent>
            </Tooltip>
        );
    }
    return (
        <TagComponent {...tagComponentProps}>
            <Box display="flex" alignItems="center" maxWidth="100%">
                {Icon ? (
                    <div>
                        <Icon height={12} width={12} />
                    </div>
                ) : null}

                {children ? (
                    <TruncatedText ref={textRef} variant="extraSmall" fontWeight={500} paddingX={2} display="block">
                        {children}
                    </TruncatedText>
                ) : null}

                {onRemoveClick ? (
                    <RemoveButton
                        type="button"
                        disabled={rest.disabled}
                        onClick={rest.disabled ? undefined : (e) => onRemoveClick(e)}
                    >
                        <IconX height={12} width={12} />
                    </RemoveButton>
                ) : null}
            </Box>
        </TagComponent>
    );
};
